import { Alert, Button, Icon, TextInput, TextLink } from '@mgmresorts/mgm-ui';
import { navigate } from 'hookrouter';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { KnownError } from '../../../api/errors';
import { mlifeVerificationCodeRequest } from '../../../api/fetch/mlife-activation';
import { Container } from '../../../components/ui/Container';
import { Logo } from '../../../components/ui/Logo';
import { PageLayout } from '../../../components/ui/PageLayout';
import { useContent } from '../../../hooks/useContent';
import { useFieldValidation } from '../../../hooks/useFieldValidation';
import { useInput } from '../../../hooks/useInput';
import { useLoadingLabel } from '../../../hooks/useLoadingLabel';
import { captureEvent, captureException } from '../../../monitoring/logrocket';
import { useEvent } from '../../../utils/analytics/useEvent';
import { activationCodeValidator } from '../../../utils/validators';
import { MlifeActivationContext } from '../MlifeActivationContext';

import {
  StyledContent,
  StyledHeading,
  StyledInfo,
  StyledInputContainer,
  StyledSuccessInfo,
  StyledAlertContainer,
} from './styles';

export function MlifeVerification() {
  const trackEvent = useEvent();
  const { activationRequest, resetActivationForm, setActivationRequest, setStep } = useContext(MlifeActivationContext);
  const { validateField, invalidFields } = useFieldValidation([activationCodeValidator]);
  const { bind: bindActivationCode, value: activationCode } = useInput('', 'activationCode', undefined, validateField);
  const { getContentByPath } = useContent();
  const [trackedError, setTrackedError] = useState('');
  const [error, setErrorState] = useState<null | string>(null);
  const [verificationState, setVerificationState] = useState<'init' | 'activating' | 'success' | 'error'>('init');
  const verifyActivateCTA = useLoadingLabel(verificationState === 'activating', 'mlifeActivation.verifySection.cta');

  useEffect(() => {
    if (!activationRequest) {
      navigate('activate');
    }
  }, [activationRequest]);

  const errorMessage = useMemo<string>(
    () =>
      invalidFields.includes('activationCode')
        ? !activationCode || activationCode.length === 0
          ? getContentByPath(`mlifeActivation.fields.activationCode.required`)
          : getContentByPath(`mlifeActivation.fields.activationCode.error`)
        : '',
    [activationCode, getContentByPath, invalidFields],
  );

  useEffect(() => {
    if (errorMessage !== trackedError) {
      trackEvent(
        'identity – verification email code - failure',
        'verification email input error',
        'verifyemailinputerror',
        {
          errorDisplayed: errorMessage,
        },
      );
      setTrackedError(errorMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorMessage, trackEvent, setTrackedError]);

  const requestMlifeActivation = useCallback(async () => {
    try {
      if (!activationRequest) {
        return null;
      }

      setVerificationState('activating');

      captureEvent('Activation: Verification', {
        email: String(activationRequest.login),
      });

      const guestInfo = await mlifeVerificationCodeRequest({
        activationCode,
        activationKid: activationRequest.activationKid,
        login: activationRequest.login,
      });

      setVerificationState('success');

      setActivationRequest({
        ...activationRequest,
        ...guestInfo,
      });

      await new Promise(resolve => {
        setTimeout(() => {
          resolve(true);
        }, 3000);
      });

      setStep('accountSetup');
      trackEvent('identity – verification email code - success', 'valid verification code', 'verifyemailsuccess');
    } catch (error) {
      setVerificationState('error');

      let message: string;
      if (error instanceof KnownError) {
        message = error.message;
      } else {
        message = getContentByPath('mlifeActivation.verifySection.error');
      }

      setErrorState(message);

      trackEvent('identity – verification email code - failure', 'invalid verification code', 'verifyemailfail', {
        errorDisplayed: message,
      });
      captureException(error as Error, 'activate', {
        username: String(activationRequest?.login),
        step: 'activate-request',
      });
    }
  }, [activationCode, activationRequest, getContentByPath, setActivationRequest, setStep, trackEvent]);

  if (verificationState === 'success') {
    return (
      <StyledContent>
        <PageLayout
          title={getContentByPath('mlifeActivation.verifySection.title')}
          description={getContentByPath('mlifeActivation.description')}
        >
          <Container height='auto'>
            <Icon name='checkmark-circle' variant='filled' size='large' />
            <StyledSuccessInfo variant='label-regular-l' as='p' className='identity__email-verified-success-info'>
              {getContentByPath('mlifeActivation.verifySection.successInfo')}
            </StyledSuccessInfo>
          </Container>
        </PageLayout>
      </StyledContent>
    );
  }

  return (
    <StyledContent>
      <PageLayout
        title={getContentByPath('mlifeActivation.verifySection.title')}
        description={getContentByPath('mlifeActivation.description')}
      >
        <Container height='auto'>
          <Logo />

          <StyledHeading variant='body-medium-l' as='h1' className='identity__page-heading'>
            {getContentByPath('mlifeActivation.verifySection.title')}
          </StyledHeading>

          {verificationState === 'error' && error && (
            <StyledAlertContainer>
              <Alert open variant='error' message={error} hiddenCloseButton />
            </StyledAlertContainer>
          )}

          <StyledInfo
            variant='body-regular-m'
            className='identity__verification-description'
            dangerouslySetInnerHTML={{
              __html: (getContentByPath('mlifeActivation.verifySection.body') as string).replace(
                '%email%',
                activationRequest?.login ?? '',
              ),
            }}
          />
          <StyledInputContainer>
            <TextInput
              variant='default'
              label={getContentByPath('mlifeActivation.fields.activationCode.title')}
              id='activationCode'
              testId='activationCode'
              disabled={verificationState === 'activating'}
              className={`identity__field identity__field-activation-code ${
                invalidFields.includes('activationCode') ? 'with-error' : ''
              }`}
              error={!!errorMessage}
              hint={errorMessage}
              {...bindActivationCode}
              inputMode='numeric'
              autoComplete='one-time-code'
            />
          </StyledInputContainer>

          {activationCode.length === 0 && (
            <StyledAlertContainer>
              <Alert
                open
                variant='informative'
                message={getContentByPath('mlifeActivation.verifySection.message')}
                hiddenCloseButton
                actions={
                  <TextLink button variant='small' onClick={resetActivationForm}>
                    {getContentByPath('mlifeActivation.verifySection.resend')}
                  </TextLink>
                }
              />
            </StyledAlertContainer>
          )}

          <Button
            variant='primary'
            size='large'
            testId='activate-button'
            label={verifyActivateCTA}
            onClick={requestMlifeActivation}
            className='form-button identity__form-submit'
            disabled={!activationCode || invalidFields.includes('activationCode')}
            loading={verificationState === 'activating'}
            fullWidth
          />
        </Container>
      </PageLayout>
    </StyledContent>
  );
}
